import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ComposedChart,
} from 'recharts';
import {
  Zap, Droplets, Flame, Bed, AlertTriangle, TrendingUp, Plus, Settings as SettingsIcon,
  Activity, Loader2, Trash2, Edit2, BarChart3, Sparkles, Trophy, AlertCircle,
  FileSpreadsheet, FileText, Upload, Download, Info,
} from 'lucide-react';

const MONTH_NAMES = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const UTILITY_THEME = {
  energia: { color: '#F59E0B', light: '#FEF3C7', dark: '#92400E', icon: Zap, label: 'Energia', unit: 'kWh' },
  acqua: { color: '#3B82F6', light: '#DBEAFE', dark: '#1E40AF', icon: Droplets, label: 'Acqua', unit: 'm³' },
  gas: { color: '#EF4444', light: '#FEE2E2', dark: '#991B1B', icon: Flame, label: 'Gas', unit: 'Smc' },
};

const SEVERITY_STYLES = {
  red: { ring: 'ring-red-500/40', border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', pulse: 'animate-pulse' },
  orange: { ring: 'ring-orange-400/40', border: 'border-orange-400', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400', pulse: '' },
  yellow: { ring: 'ring-amber-400/40', border: 'border-amber-300', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', pulse: '' },
};

const KPICard = ({ icon: Icon, label, value, unit, accent, delay = 0, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    data-testid={testid}
  >
    <Card className="relative overflow-hidden border-slate-200 hover:shadow-lg transition-shadow group">
      <div className="absolute right-0 top-0 h-full w-1.5 transition-all group-hover:w-2" style={{ background: accent }} />
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-2xl font-black text-slate-900 mt-1 tabular-nums">
          {value !== null && value !== undefined ? Number(value).toLocaleString('it-IT', { maximumFractionDigits: 2 }) : '—'}
          {unit && <span className="text-sm font-semibold text-slate-400 ml-1">{unit}</span>}
        </p>
      </CardContent>
    </Card>
  </motion.div>
);

const AlertCard = ({ alert, idx }) => {
  const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.yellow;
  const theme = UTILITY_THEME[alert.utility] || UTILITY_THEME.energia;
  const Icon = theme.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: idx * 0.04 }}
      data-testid={`alert-card-${alert.id}`}
    >
      <Card className={`relative border-l-4 ${style.border} hover:shadow-md transition-shadow`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center`}>
              <Icon className="w-5 h-5" style={{ color: theme.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase ${style.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot} ${style.pulse}`} />
                  {alert.severity === 'red' ? 'Critico' : alert.severity === 'orange' ? 'Attenzione' : 'Anomalia'}
                </span>
                <Badge variant="secondary" className="text-[10px]">{MONTH_NAMES[alert.month - 1]} {alert.year}</Badge>
              </div>
              <p className="font-bold text-slate-900 text-sm">{alert.title}</p>
              <p className="text-xs text-slate-600 mt-1">{alert.message}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-[11px]">
                <span className="text-slate-500">Atteso: <span className="font-semibold text-slate-700 tabular-nums">{alert.expected}</span></span>
                <span className="text-slate-500">Reale: <span className="font-semibold text-slate-900 tabular-nums">{alert.actual}</span></span>
                <span className={`font-bold tabular-nums ${alert.deviation_pct > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {alert.deviation_pct > 0 ? '+' : ''}{alert.deviation_pct.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ChartCard = ({ title, icon: Icon, children, accent = '#1E3A8A' }) => (
  <Card className="border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
          {Icon && <Icon className="w-4 h-4" />}
        </div>
        <h3 className="font-bold text-slate-900">{title}</h3>
      </div>
      <div className="h-72">{children}</div>
    </CardContent>
  </Card>
);

const ConsumptionPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = ['super_admin', 'sede'].includes(user?.role);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('all');
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Dashboard
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  // Storico
  const [records, setRecords] = useState([]);
  // Config
  const [hotelConfig, setHotelConfig] = useState(null);
  // Insert dialog
  const [insertOpen, setInsertOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ hotel_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, occupied_rooms: '', energy_kwh: '', water_m3: '', gas_smc: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  // Benchmark
  const [benchmarkData, setBenchmarkData] = useState(null);

  // Efficiency trend (singolo hotel)
  const [effTrend, setEffTrend] = useState(null);

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  // ============ Load hotels
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/hotels');
        setHotels(data);
        if (data.length > 0 && !isSuperAdmin) setSelectedHotel(data[0].id);
      } catch (e) { toast.error('Errore caricamento hotel'); }
      finally { setLoading(false); }
    })();
  }, [isSuperAdmin]);

  // ============ Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const params = selectedHotel !== 'all' ? { hotel_id: selectedHotel } : {};
      const [dRes, aRes] = await Promise.all([
        api.get('/consumption/dashboard', { params }),
        api.get('/consumption/alerts', { params }),
      ]);
      setDashboard(dRes.data);
      setAlerts(aRes.data.items || []);
    } catch (e) { toast.error('Errore dashboard consumi'); }
  }, [selectedHotel]);

  const fetchRecords = useCallback(async () => {
    try {
      const params = selectedHotel !== 'all' ? { hotel_id: selectedHotel } : {};
      const { data } = await api.get('/consumption', { params });
      setRecords(data.items || []);
    } catch (e) { toast.error('Errore caricamento storico'); }
  }, [selectedHotel]);

  const fetchHotelConfig = useCallback(async () => {
    if (selectedHotel === 'all') { setHotelConfig(null); return; }
    try {
      const { data } = await api.get(`/consumption/hotel-config/${selectedHotel}`);
      setHotelConfig(data);
    } catch (e) { setHotelConfig(null); }
  }, [selectedHotel]);

  const fetchBenchmark = useCallback(async () => {
    try {
      const { data } = await api.get('/consumption/benchmark');
      setBenchmarkData(data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchEffTrend = useCallback(async () => {
    if (selectedHotel === 'all') { setEffTrend(null); return; }
    try {
      const { data } = await api.get('/consumption/efficiency-trend', { params: { hotel_id: selectedHotel } });
      setEffTrend(data);
    } catch (e) { setEffTrend(null); }
  }, [selectedHotel]);

  // Download blob helper (Excel/PDF)
  const downloadBlob = async (url, filename, mime) => {
    try {
      const params = selectedHotel !== 'all' ? { hotel_id: selectedHotel } : {};
      const res = await api.get(url, { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: mime });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success(`${filename} scaricato`);
    } catch (e) { toast.error('Errore download'); }
  };

  const handleExportExcel = () => downloadBlob('/consumption/export/excel', `consumi_${new Date().toISOString().slice(0, 10)}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  const handleExportPdf = () => downloadBlob('/consumption/export/pdf', `consumi_${new Date().toISOString().slice(0, 10)}.pdf`, 'application/pdf');
  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/consumption/import/template', { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.download = 'template_consumi.xlsx';
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (e) { toast.error('Errore download template'); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/consumption/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      toast.success(`Import completato: ${data.inserted} inseriti · ${data.skipped} saltati`);
      fetchDashboard(); fetchRecords(); fetchBenchmark();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore import');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (selectedHotel) {
      fetchDashboard();
      fetchRecords();
      fetchHotelConfig();
      fetchEffTrend();
    }
    fetchBenchmark();
  }, [selectedHotel, fetchDashboard, fetchRecords, fetchHotelConfig, fetchBenchmark, fetchEffTrend]);

  // ============ Submit insert/edit
  const submitRecord = async (e) => {
    e?.preventDefault();
    if (!form.hotel_id) { toast.error('Seleziona hotel'); return; }
    if (!form.occupied_rooms) { toast.error('Camere occupate obbligatorio'); return; }
    if (!form.energy_kwh && !form.water_m3 && !form.gas_smc) { toast.error('Inserisci almeno una utenza'); return; }
    setSubmitting(true);
    try {
      const payload = {
        hotel_id: form.hotel_id,
        year: Number(form.year),
        month: Number(form.month),
        occupied_rooms: Number(form.occupied_rooms),
        energy_kwh: form.energy_kwh ? Number(form.energy_kwh) : null,
        water_m3: form.water_m3 ? Number(form.water_m3) : null,
        gas_smc: form.gas_smc ? Number(form.gas_smc) : null,
        notes: form.notes || null,
      };
      if (editingId) {
        // PUT only updatable fields
        const upd = { occupied_rooms: payload.occupied_rooms, energy_kwh: payload.energy_kwh, water_m3: payload.water_m3, gas_smc: payload.gas_smc, notes: payload.notes };
        await api.put(`/consumption/${editingId}`, upd);
        toast.success('Record aggiornato');
      } else {
        await api.post('/consumption', payload);
        toast.success('Record consumi salvato');
      }
      setInsertOpen(false);
      setEditingId(null);
      setForm({ hotel_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, occupied_rooms: '', energy_kwh: '', water_m3: '', gas_smc: '', notes: '' });
      fetchDashboard(); fetchRecords();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSubmitting(false); }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Eliminare definitivamente questo record?')) return;
    try {
      await api.delete(`/consumption/${id}`);
      toast.success('Record eliminato');
      fetchDashboard(); fetchRecords();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  const startEdit = (rec) => {
    setEditingId(rec.id);
    setForm({
      hotel_id: rec.hotel_id, year: rec.year, month: rec.month,
      occupied_rooms: rec.occupied_rooms ?? '', energy_kwh: rec.energy_kwh ?? '',
      water_m3: rec.water_m3 ?? '', gas_smc: rec.gas_smc ?? '', notes: rec.notes ?? '',
    });
    setInsertOpen(true);
  };

  const saveConfig = async (patch) => {
    if (!hotelConfig || !isSuperAdmin) return;
    try {
      const next = { ...hotelConfig, ...patch };
      await api.put(`/consumption/hotel-config/${selectedHotel}`, {
        energy_active: next.energy_active, water_active: next.water_active, gas_active: next.gas_active,
      });
      setHotelConfig(next);
      toast.success('Configurazione aggiornata');
    } catch (e) { toast.error('Errore configurazione'); }
  };

  const hotelMap = useMemo(() => Object.fromEntries(hotels.map(h => [h.id, h])), [hotels]);
  const trend = dashboard?.trend_12 || [];
  const current = dashboard?.current;
  const summary = dashboard?.summary || { records: 0, occupied_rooms_total: 0, energy_kwh_total: 0, water_m3_total: 0, gas_smc_total: 0, alerts_count: 0 };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-900" /></div>;

  return (
    <div className="space-y-6" data-testid="consumption-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-blue-900" />
            Consumi Hotel
          </h1>
          <p className="text-slate-500 mt-1">Efficienza operativa · KPI normalizzati · Alert anomalie</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={selectedHotel} onValueChange={setSelectedHotel}>
            <SelectTrigger className="w-56" data-testid="consumption-hotel-select">
              <SelectValue placeholder="Seleziona hotel" />
            </SelectTrigger>
            <SelectContent>
              {isSuperAdmin && <SelectItem value="all">Tutti gli hotel</SelectItem>}
              {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {isSuperAdmin && (
            <>
              <Button variant="outline" onClick={handleExportExcel} className="gap-2" data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </Button>
              <Button variant="outline" onClick={handleExportPdf} className="gap-2" data-testid="export-pdf-btn">
                <FileText className="w-4 h-4" /> PDF
              </Button>
              <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="open-import-dialog">
                <Upload className="w-4 h-4" /> Import
              </Button>
              <Button onClick={() => { setEditingId(null); setForm(f => ({ ...f, hotel_id: selectedHotel !== 'all' ? selectedHotel : '' })); setInsertOpen(true); }} className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="open-insert-dialog">
                <Plus className="w-4 h-4" /> Inserisci Consumi
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="dashboard" className="rounded-lg" data-testid="tab-dashboard"><BarChart3 className="w-4 h-4 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="storico" className="rounded-lg" data-testid="tab-storico"><TrendingUp className="w-4 h-4 mr-1.5" /> Storico</TabsTrigger>
          <TabsTrigger value="alert" className="rounded-lg" data-testid="tab-alert">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Alert
            {alerts.length > 0 && <Badge className="ml-2 bg-red-600 text-white text-[10px] px-1.5">{alerts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="benchmark" className="rounded-lg" data-testid="tab-benchmark"><Trophy className="w-4 h-4 mr-1.5" /> Benchmark</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="config" className="rounded-lg" data-testid="tab-config"><SettingsIcon className="w-4 h-4 mr-1.5" /> Configurazione</TabsTrigger>}
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6 mt-6">
          {/* KPI Cards row 1 — Ultimo mese */}
          {current ? (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Ultimo mese registrato: {MONTH_NAMES[current.month - 1]} {current.year}
                {current.hotel_name && ` · ${current.hotel_name}`}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard icon={Bed} label="Camere occupate" value={current.occupied_rooms} unit="" accent="#1E3A8A" delay={0} testid="kpi-occupied" />
                <KPICard icon={Sparkles} label="Occupazione" value={current.occupancy_pct} unit="%" accent="#10B981" delay={0.05} testid="kpi-occupancy" />
                <KPICard icon={Zap} label="kWh / camera" value={current.kwh_per_room} unit="kWh" accent={UTILITY_THEME.energia.color} delay={0.1} testid="kpi-kwh-room" />
                <KPICard icon={Droplets} label="m³ / camera" value={current.m3_per_room} unit="m³" accent={UTILITY_THEME.acqua.color} delay={0.15} testid="kpi-m3-room" />
              </div>
            </div>
          ) : (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="p-8 text-center">
                <Activity className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">Nessun consumo registrato</p>
                {isSuperAdmin && <p className="text-xs text-slate-400 mt-1">Clicca "Inserisci Consumi" per iniziare</p>}
              </CardContent>
            </Card>
          )}

          {/* Totali 12 mesi */}
          {summary.records > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Totali ultimi 12 mesi</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard icon={Zap} label="Energia totale" value={summary.energy_kwh_total} unit="kWh" accent={UTILITY_THEME.energia.color} delay={0} />
                <KPICard icon={Droplets} label="Acqua totale" value={summary.water_m3_total} unit="m³" accent={UTILITY_THEME.acqua.color} delay={0.05} />
                <KPICard icon={Flame} label="Gas totale" value={summary.gas_smc_total} unit="Smc" accent={UTILITY_THEME.gas.color} delay={0.1} />
                <KPICard icon={AlertTriangle} label="Alert attivi" value={summary.alerts_count} unit="" accent={summary.alerts_count > 0 ? '#EF4444' : '#10B981'} delay={0.15} />
              </div>
            </div>
          )}

          {/* Trend Indice Efficienza (solo se hotel singolo selezionato) */}
          {selectedHotel !== 'all' && effTrend && effTrend.items && effTrend.items.length >= 2 && (
            <Card className="border-slate-200" data-testid="efficiency-trend-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#10B98118', color: '#10B981' }}>
                        <TrendingUp className="w-4 h-4" />
                      </div>
                      <h3 className="font-bold text-slate-900 text-base">Trend Indice Efficienza Energetica</h3>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-11">Finestra mobile 12 mesi · ROI degli interventi energetici nel tempo</p>
                  </div>
                  {effTrend.delta !== null && effTrend.delta !== undefined && (
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Da {effTrend.first_label} a {effTrend.last_label}</p>
                        <p className={`text-2xl font-black tabular-nums ${effTrend.delta > 0 ? 'text-emerald-600' : effTrend.delta < 0 ? 'text-red-600' : 'text-slate-500'}`} data-testid="efficiency-trend-delta">
                          {effTrend.delta > 0 ? '+' : ''}{effTrend.delta}
                        </p>
                        <p className="text-[10px] text-slate-400">punti indice</p>
                      </div>
                      <div className={`w-px h-12 bg-slate-200`} />
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Ultimo</p>
                        <p className="text-2xl font-black text-slate-900 tabular-nums" data-testid="efficiency-trend-last">{effTrend.last_eff ?? '—'}</p>
                        <p className="text-[10px] text-slate-400">/ 100</p>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={effTrend.items} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="effFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <RTooltip
                        contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }}
                        formatter={(val, name) => [val ?? '—', name]}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="efficiency_index" name="Indice (effettivo)" stroke="#10B981" strokeWidth={2.5} fill="url(#effFill)" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      {effTrend.items.some(i => i.all_electric) && (
                        <Line type="monotone" dataKey="efficiency_index_legacy" name="Indice (k=50 base)" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2.5">
                  <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <span>
                    <b>Come leggerlo:</b> ogni punto è l&apos;indice calcolato sui 12 mesi terminanti in quella data.
                    Una curva in salita = gli interventi (LED, HVAC, isolamento) stanno funzionando.
                    {effTrend.items.some(i => i.all_electric) && <> La linea tratteggiata grigia mostra come sarebbe l&apos;indice senza correzione All-Electric.</>}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Charts */}
          {trend.length > 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Consumi mensili (assoluti)" icon={TrendingUp} accent="#1E3A8A">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trend}>
                    <defs>
                      <linearGradient id="gEnergy" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={UTILITY_THEME.energia.color} stopOpacity={0.5} /><stop offset="100%" stopColor={UTILITY_THEME.energia.color} stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <RTooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="energy_kwh" name="kWh" stroke={UTILITY_THEME.energia.color} fill="url(#gEnergy)" strokeWidth={2} />
                    <Bar dataKey="water_m3" name="m³" fill={UTILITY_THEME.acqua.color} opacity={0.7} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="gas_smc" name="Smc" fill={UTILITY_THEME.gas.color} opacity={0.7} radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="KPI per camera (normalizzati)" icon={Activity} accent="#10B981">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <RTooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="kwh_per_room" name="kWh/cam" stroke={UTILITY_THEME.energia.color} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="m3_per_room" name="m³/cam" stroke={UTILITY_THEME.acqua.color} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="smc_per_room" name="Smc/cam" stroke={UTILITY_THEME.gas.color} strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Occupazione vs Energia" icon={Sparkles} accent="#8B5CF6">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <RTooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="l" dataKey="occupancy_pct" name="Occupazione %" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="r" type="monotone" dataKey="kwh_per_room" name="kWh/cam" stroke={UTILITY_THEME.energia.color} strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Top alert recenti" icon={AlertTriangle} accent="#EF4444">
                {alerts.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <div className="text-center">
                      <Sparkles className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-slate-600">Nessun alert attivo</p>
                      <p className="text-xs">Consumi in linea con la media storica</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2.5 overflow-y-auto h-full pr-2">
                    {alerts.slice(0, 5).map((a, i) => <AlertCard key={a.id} alert={a} idx={i} />)}
                  </div>
                )}
              </ChartCard>
            </div>
          )}
        </TabsContent>

        {/* STORICO */}
        <TabsContent value="storico" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {records.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Nessun record disponibile</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-[11px] font-bold uppercase text-slate-500">
                        <th className="px-4 py-3">Hotel</th>
                        <th className="px-4 py-3">Periodo</th>
                        <th className="px-4 py-3 text-right">Occupate</th>
                        <th className="px-4 py-3 text-right">Occ %</th>
                        <th className="px-4 py-3 text-right">kWh</th>
                        <th className="px-4 py-3 text-right">m³</th>
                        <th className="px-4 py-3 text-right">Smc</th>
                        <th className="px-4 py-3 text-right">kWh/cam</th>
                        {isSuperAdmin && <th className="px-4 py-3"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`storico-row-${r.id}`}>
                          <td className="px-4 py-3 font-semibold text-slate-900">{r.hotel_name || hotelMap[r.hotel_id]?.name || '—'}</td>
                          <td className="px-4 py-3"><Badge variant="secondary">{MONTH_NAMES[r.month - 1]} {r.year}</Badge></td>
                          <td className="px-4 py-3 text-right tabular-nums">{r.occupied_rooms}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{r.kpis?.occupancy_pct ?? '—'}%</td>
                          <td className="px-4 py-3 text-right tabular-nums">{r.energy_kwh ?? '—'}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{r.water_m3 ?? '—'}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{r.gas_smc ?? '—'}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-bold text-amber-600">{r.kpis?.kwh_per_room ?? '—'}</td>
                          {isSuperAdmin && (
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-record-${r.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteRecord(r.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-record-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ALERT */}
        <TabsContent value="alert" className="mt-6 space-y-3">
          {alerts.length === 0 ? (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                <p className="font-bold text-emerald-700">Nessuna anomalia rilevata</p>
                <p className="text-sm text-emerald-600 mt-1">I consumi sono in linea con la media storica.</p>
              </CardContent>
            </Card>
          ) : (
            alerts.map((a, i) => <AlertCard key={a.id} alert={a} idx={i} />)
          )}
        </TabsContent>

        {/* BENCHMARK */}
        <TabsContent value="benchmark" className="mt-6 space-y-5">
          {!benchmarkData || benchmarkData.items.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="p-8 text-center text-slate-500">
                <Trophy className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p>Nessun dato disponibile per il benchmark</p>
                <p className="text-xs text-slate-400 mt-1">Inserisci consumi per almeno 2 hotel per attivare il confronto</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Info banner: come è calcolato l'indice */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="benchmark-info-banner">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div className="text-xs text-blue-900 leading-relaxed">
                  <b>Come leggere l&apos;Indice Efficienza (0–100):</b> media pesata di kWh/cam (45%), m³/cam (30%) e Smc/cam (25%) sugli ultimi 12 mesi.
                  Hotel <b>senza gas</b> sono identificati come <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold"><Zap className="w-3 h-3"/>All-Electric</span> e
                  valutati con costanti calibrate (riscaldamento elettrico → kWh fisiologicamente più alti).
                </div>
              </div>

              {/* Top 3 Efficient + Top 3 Investigate */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Trophy className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-bold text-emerald-900">Top 3 più efficienti</h3>
                    </div>
                    <div className="space-y-2">
                      {benchmarkData.top_efficient.map((r, i) => (
                        <motion.div
                          key={r.hotel_id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white border border-emerald-100"
                          data-testid={`benchmark-top-efficient-${i}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 font-black text-sm flex items-center justify-center">#{r.rank}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-900 truncate flex items-center gap-1.5">
                              {r.hotel_name}
                              {r.all_electric && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800" title="Hotel senza gas: indice calibrato per riscaldamento elettrico (k=80 kWh)">
                                  <Zap className="w-2.5 h-2.5"/>ALL-ELECTRIC
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500">{r.city || ''} · {r.total_rooms} camere</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-black text-emerald-600 tabular-nums">{r.efficiency_index ?? '—'}</p>
                            <p className="text-[10px] text-emerald-700 uppercase tracking-wider">Eff.</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <h3 className="font-bold text-amber-900">Top 3 da investigare</h3>
                    </div>
                    <div className="space-y-2">
                      {benchmarkData.top_investigate.map((r, i) => (
                        <motion.div
                          key={r.hotel_id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white border border-amber-100"
                          data-testid={`benchmark-top-investigate-${i}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-black text-sm flex items-center justify-center">#{r.rank}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-900 truncate flex items-center gap-1.5">
                              {r.hotel_name}
                              {r.all_electric && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800" title="Hotel senza gas: indice calibrato per riscaldamento elettrico (k=80 kWh)">
                                  <Zap className="w-2.5 h-2.5"/>ALL-ELECTRIC
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500">{r.city || ''} · {r.total_rooms} camere</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-black text-amber-600 tabular-nums">{r.efficiency_index ?? '—'}</p>
                            <p className="text-[10px] text-amber-700 uppercase tracking-wider">Eff.</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tabella comparativa completa */}
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-900" />
                    <h3 className="font-bold text-slate-900">Tabella comparativa</h3>
                    <Badge variant="secondary" className="ml-auto text-xs">{benchmarkData.total_hotels} hotel · media ultimi 12 mesi</Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left text-[11px] font-bold uppercase text-slate-500">
                          <th className="px-4 py-3">Rank</th>
                          <th className="px-4 py-3">Hotel</th>
                          <th className="px-4 py-3 text-right">Camere</th>
                          <th className="px-4 py-3 text-right">Occ %</th>
                          <th className="px-4 py-3 text-right">kWh/cam</th>
                          <th className="px-4 py-3 text-right">m³/cam</th>
                          <th className="px-4 py-3 text-right">Smc/cam</th>
                          <th className="px-4 py-3 text-right">Eff. Index</th>
                        </tr>
                      </thead>
                      <tbody>
                        {benchmarkData.items.map((r) => {
                          const isTop3 = r.rank <= 3;
                          const isBottom3 = r.rank > benchmarkData.items.length - 3 && benchmarkData.items.length > 3;
                          return (
                            <tr key={r.hotel_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`benchmark-row-${r.hotel_id}`}>
                              <td className="px-4 py-3">
                                <Badge className={isTop3 ? 'bg-emerald-600 text-white' : isBottom3 ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'}>#{r.rank}</Badge>
                              </td>
                              <td className="px-4 py-3 font-semibold text-slate-900">
                                <span className="inline-flex items-center gap-1.5 flex-wrap">
                                  {r.hotel_name}
                                  <span className="text-xs text-slate-400 font-normal">· {r.city || ''}</span>
                                  {r.all_electric && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800" title="Hotel senza gas: indice calibrato per riscaldamento elettrico (k=80 kWh)">
                                      <Zap className="w-2.5 h-2.5"/>ALL-ELECTRIC
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums">{r.total_rooms}</td>
                              <td className="px-4 py-3 text-right tabular-nums">{r.occupancy_pct_avg}%</td>
                              <td className="px-4 py-3 text-right tabular-nums font-semibold text-amber-600">{r.avg_kwh_per_room ?? '—'}</td>
                              <td className="px-4 py-3 text-right tabular-nums font-semibold text-blue-600">{r.avg_m3_per_room ?? '—'}</td>
                              <td className="px-4 py-3 text-right tabular-nums font-semibold text-red-600">{r.avg_smc_per_room ?? '—'}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold tabular-nums ${
                                  isTop3 ? 'bg-emerald-100 text-emerald-700' : isBottom3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                                }`}>
                                  {r.efficiency_index ?? '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* CONFIGURAZIONE */}
        {isSuperAdmin && (
          <TabsContent value="config" className="mt-6">
            {selectedHotel === 'all' ? (
              <Card className="border-dashed border-slate-300">
                <CardContent className="p-6 text-center text-slate-500">Seleziona un hotel per configurare le utenze attive</CardContent>
              </Card>
            ) : hotelConfig ? (
              <Card className="border-slate-200">
                <CardContent className="p-6 space-y-5">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Hotel</p>
                    <p className="text-xl font-black text-slate-900 mt-1">{hotelConfig.hotel_name}</p>
                    <p className="text-sm text-slate-500 mt-0.5">Camere totali: <span className="font-bold text-slate-900">{hotelConfig.total_rooms}</span> (riprese dalla configurazione Hotel)</p>
                  </div>
                  <div className="border-t border-slate-100 pt-5 space-y-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Utenze monitorate</p>
                    {[
                      { key: 'energy_active', label: 'Energia elettrica', icon: Zap, color: UTILITY_THEME.energia.color },
                      { key: 'water_active', label: 'Acqua', icon: Droplets, color: UTILITY_THEME.acqua.color },
                      { key: 'gas_active', label: 'Gas', icon: Flame, color: UTILITY_THEME.gas.color },
                    ].map(u => {
                      const UIcon = u.icon;
                      return (
                        <div key={u.key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${u.color}18`, color: u.color }}>
                              <UIcon className="w-4 h-4" />
                            </div>
                            <span className="font-semibold text-slate-900">{u.label}</span>
                          </div>
                          <Switch
                            checked={!!hotelConfig[u.key]}
                            onCheckedChange={(v) => saveConfig({ [u.key]: v })}
                            data-testid={`config-switch-${u.key}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-900 mx-auto" /></div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* INSERT DIALOG */}
      <Dialog open={insertOpen} onOpenChange={(o) => { setInsertOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Edit2 className="w-5 h-5 text-blue-900" /> : <Plus className="w-5 h-5 text-blue-900" />}
              {editingId ? 'Modifica Consumi' : 'Nuovo Inserimento Consumi'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitRecord} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Hotel *</Label>
                <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v })} disabled={!!editingId}>
                  <SelectTrigger data-testid="insert-hotel-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Anno *</Label>
                <Input type="number" min="2000" max="2100" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} disabled={!!editingId} data-testid="insert-year" />
              </div>
              <div>
                <Label>Mese *</Label>
                <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: v })} disabled={!!editingId}>
                  <SelectTrigger data-testid="insert-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Camere occupate (totale del mese) *</Label>
              <Input type="number" min="0" value={form.occupied_rooms} onChange={(e) => setForm({ ...form, occupied_rooms: e.target.value })} placeholder="es. 1450" required data-testid="insert-occupied" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" style={{ color: UTILITY_THEME.energia.color }} /> kWh</Label>
                <Input type="number" step="0.01" min="0" value={form.energy_kwh} onChange={(e) => setForm({ ...form, energy_kwh: e.target.value })} placeholder="es. 15000" data-testid="insert-energy" />
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Droplets className="w-3.5 h-3.5" style={{ color: UTILITY_THEME.acqua.color }} /> m³ acqua</Label>
                <Input type="number" step="0.01" min="0" value={form.water_m3} onChange={(e) => setForm({ ...form, water_m3: e.target.value })} placeholder="es. 450" data-testid="insert-water" />
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" style={{ color: UTILITY_THEME.gas.color }} /> Smc gas</Label>
                <Input type="number" step="0.01" min="0" value={form.gas_smc} onChange={(e) => setForm({ ...form, gas_smc: e.target.value })} placeholder="es. 3200" data-testid="insert-gas" />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note opzionali" rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInsertOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-blue-900 hover:bg-blue-800" disabled={submitting} data-testid="insert-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingId ? 'Aggiorna' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* IMPORT DIALOG */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-900" />
              Import Consumi da Excel / CSV
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm space-y-2">
              <p className="font-semibold text-blue-900">Formato richiesto:</p>
              <p className="text-blue-800 text-xs">Colonne obbligatorie: <code className="bg-white px-1.5 py-0.5 rounded">hotel_name</code> (o <code className="bg-white px-1.5 py-0.5 rounded">hotel_id</code>), <code className="bg-white px-1.5 py-0.5 rounded">year</code>, <code className="bg-white px-1.5 py-0.5 rounded">month</code>, <code className="bg-white px-1.5 py-0.5 rounded">occupied_rooms</code></p>
              <p className="text-blue-800 text-xs">Colonne opzionali: <code className="bg-white px-1.5 py-0.5 rounded">energy_kwh</code>, <code className="bg-white px-1.5 py-0.5 rounded">water_m3</code>, <code className="bg-white px-1.5 py-0.5 rounded">gas_smc</code>, <code className="bg-white px-1.5 py-0.5 rounded">notes</code> (almeno una utenza)</p>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-2 mt-1" data-testid="download-template-btn">
                <Download className="w-3.5 h-3.5" /> Scarica template Excel
              </Button>
            </div>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Carica file (.xlsx o .csv)</span>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImportFile}
                disabled={importing}
                className="block w-full mt-2 text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-blue-900 file:text-white hover:file:bg-blue-800 file:cursor-pointer"
                data-testid="import-file-input"
              />
            </label>
            {importing && (
              <div className="flex items-center gap-2 text-sm text-blue-900">
                <Loader2 className="w-4 h-4 animate-spin" />
                Importazione in corso...
              </div>
            )}
            {importResult && (
              <div className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid="import-result">
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="p-2 rounded bg-emerald-50">
                    <p className="text-2xl font-black text-emerald-700">{importResult.inserted}</p>
                    <p className="text-[10px] uppercase tracking-wider text-emerald-600">Inseriti</p>
                  </div>
                  <div className="p-2 rounded bg-amber-50">
                    <p className="text-2xl font-black text-amber-700">{importResult.skipped}</p>
                    <p className="text-[10px] uppercase tracking-wider text-amber-600">Saltati</p>
                  </div>
                  <div className="p-2 rounded bg-slate-50">
                    <p className="text-2xl font-black text-slate-700">{importResult.total_rows}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Totale righe</p>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="text-xs text-slate-600 max-h-40 overflow-y-auto">
                    <p className="font-bold mb-1">Dettagli ({importResult.errors.length}):</p>
                    {importResult.errors.map((er, i) => <p key={i} className="text-amber-700">· {er}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setImportOpen(false)} variant="outline">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsumptionPage;
